import Button from '@wiggui/ds/Button'
import { useEffect, useRef, useState } from 'react'
import { MdOutlineCampaign, MdOutlineClose } from 'react-icons/md'
import { useIntercom } from '../../hooks/useIntercom'
import { BoostIcon } from './boost-icon'
import { BoostIconMobile } from './boost-icon-mobile'

const TILE_WIDTH = 26.667
const TILE_HEIGHT = 33.333

interface Position {
  x: number
  y: number
  opacity: number
}

export const BoostBanner = ({ onClose }: { onClose: () => void }) => {
  const [positions, setPositions] = useState<Position[]>([])
  const [bannerWidth, setBannerWidth] = useState(800)
  const bannerRef = useRef<HTMLDivElement>(null)
  const { showNewMessage } = useIntercom()

  const onClickBoost = () => {
    showNewMessage('Me interesa publicar en redes sociales con Wiggot Boost')
  }

  useEffect(() => {
    const updateBannerWidth = () => {
      if (bannerRef.current) {
        setBannerWidth(bannerRef.current.offsetWidth)
      }
    }

    updateBannerWidth()
    window.addEventListener('resize', updateBannerWidth)

    return () => window.removeEventListener('resize', updateBannerWidth)
  }, [])

  useEffect(() => {
    const generatePositions = () => {
      const newPositions: Position[] = []
      const maxX = Math.floor(bannerWidth / TILE_WIDTH)
      const maxY = Math.floor(100 / TILE_HEIGHT) // Altura aproximada del banner

      for (let i = 0; i < 20; i++) {
        const x = Math.floor(Math.random() * maxX)
        const y = Math.floor(Math.random() * maxY)

        if (!newPositions.some((pos) => pos.x === x && pos.y === y)) {
          const opacity = Math.min(0.08, 0.01 + (x / maxX) * 0.07)
          newPositions.push({
            x,
            y,
            opacity
          })
        }
      }

      return newPositions
    }

    setPositions(generatePositions())

    const interval = setInterval(() => {
      setPositions(generatePositions())
    }, 800)

    return () => clearInterval(interval)
  }, [bannerWidth])

  return (
    <div
      ref={bannerRef}
      className="bg-gradient-to-r from-[#03070a] to-[#0137fa] rounded-xl p-3 relative overflow-hidden w-full h-[205px] md:h-auto"
    >
      <div
        className="absolute inset-0 opacity-30 transition-all duration-300"
        style={{
          backgroundSize: `${TILE_WIDTH}px ${TILE_HEIGHT}px`,
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.15) 1px, transparent 1px)'
        }}
      />
      <div className="absolute inset-0 overflow-hidden">
        {positions.map((pos, index) => (
          <div
            key={`${pos.x}-${pos.y}-${index}`}
            className="absolute animate-pulse transition-all duration-150"
            style={{
              top: `${pos.y * TILE_HEIGHT + 1}px`,
              left: `${pos.x * TILE_WIDTH + 1}px`,
              width: `${TILE_WIDTH - 1}px`,
              height: `${TILE_HEIGHT - 1}px`,
              animationDelay: `${index * 0.1}s`,
              animationDuration: '1.5s',
              border: '0.28px solid rgba(255, 255, 255, 0.1)',
              background: `rgba(255, 255, 255, ${pos.opacity})`
            }}
          />
        ))}
      </div>

      <div className="relative md:flex hidden items-center justify-between gap-4">
        <div className="flex flex-row items-center gap-3 flex-1">
          <BoostIcon />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white text-base leading-normal whitespace-nowrap">
              Genera prospectos en redes con Boost
            </h3>
            <p className="text-white/90 text-xs leading-[18px] mt-1">
              Con <span className="font-semibold">Boost</span>, publicamos tu
              propiedad en redes sociales para que recibas <br /> 3 veces más
              prospectos al mes.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 flex-shrink-0">
          <Button
            variant="secondary"
            onClick={onClickBoost}
            size="xsmall"
            className="bg-[#03070a] border border-[#0038ff] text-white hover:bg-[#03070a]/80 min-w-[140px] px-4 py-2.5 rounded-full"
          >
            <span className="text-sm font-semibold">Contrata Boost ahora</span>
            <MdOutlineCampaign size={16} />
          </Button>

          <button
            className="p-2.5 hover:bg-white/10 rounded-full transition-colors"
            onClick={onClose}
          >
            <MdOutlineClose className="w-4 h-4 text-white min-w-[16px] min-h-[16px]" />
          </button>
        </div>
      </div>
      <div className="relative flex md:hidden flex-col gap-3 h-full w-full">
        <div className="flex w-full justify-between items-center">
          <BoostIconMobile />
          <button
            className="p-2.5 hover:bg-white/10 rounded-full transition-colors"
            onClick={onClose}
          >
            <MdOutlineClose className="w-4 h-4 text-white min-w-[16px] min-h-[16px]" />
          </button>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="font-semibold text-white text-base leading-normal whitespace-nowrap">
            Genera prospectos en redes con Boost
          </h3>
          <p className="text-white/90 text-xs leading-[18px] mt-1">
            Con <span className="font-semibold">Boost</span>, publicamos tu
            propiedad en redes sociales para que recibas 3 veces más prospectos
            al mes.
          </p>
        </div>

        <div className="flex items-center">
          <Button
            variant="secondary"
            size="xsmall"
            className="bg-[#03070a] border border-[#0038ff] text-white hover:bg-[#03070a]/80 min-w-[140px] px-4 py-2.5 rounded-full"
            onClick={onClickBoost}
          >
            <span className="text-sm font-semibold">Contrata Boost ahora</span>
            <MdOutlineCampaign size={16} />
          </Button>
        </div>
      </div>
    </div>
  )
}
